/**
 * GoLiveShareBar — sticky toolbar for the broadcaster page that surfaces
 * a real-time viewer count + one-tap share-to-social actions the moment
 * a creator hits AIR.
 *
 * Polls `/api/live/{streamId}` every 5s for `viewer_count`, animates the
 * count when it ticks up, and exposes Twitter / Facebook / Copy-link /
 * Native-share buttons (the OS share sheet on mobile, text fallback
 * everywhere else).
 *
 * Mounted by `pages/DirectorsMode.jsx` once `streamId` is available and
 * the stream is live (`is_live === true`).
 */
import React, { useEffect, useRef, useState } from "react";
import { Eye, Copy, Share2, Twitter, Facebook, Check, MessageCircle, Mail, Linkedin } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

export default function GoLiveShareBar({ streamId, title }) {
  const [count, setCount] = useState(0);
  const [delta, setDelta] = useState(0); // animation hint: +1 / +2 etc.
  const [copied, setCopied] = useState(false);
  const lastRef = useRef(0);

  useEffect(() => {
    if (!streamId) return;
    let stop = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/live/${streamId}`);
        if (stop) return;
        const next = Number(data?.viewer_count || 0);
        const prev = lastRef.current;
        if (next > prev) {
          setDelta(next - prev);
          setTimeout(() => setDelta(0), 1200);
        }
        lastRef.current = next;
        setCount(next);
      } catch { /* swallow */ }
    };
    tick();
    const h = setInterval(tick, 5000);
    return () => { stop = true; clearInterval(h); };
  }, [streamId]);

  const shareUrl = streamId ? `${window.location.origin}/live/${streamId}` : window.location.origin;
  const shareText = `🔴 LIVE NOW on Tap IN: ${title || "Watch the show"} — `;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      toast.success("Live link copied — paste it anywhere!");
    } catch { toast.error("Couldn't copy"); }
  };

  const tweet = () => {
    const u = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(u, "_blank", "noopener,noreferrer");
  };
  const fb = () => {
    const u = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
    window.open(u, "_blank", "noopener,noreferrer");
  };
  const whatsapp = () => {
    // WhatsApp handles wa.me/ links on mobile + desktop web.
    const u = `https://wa.me/?text=${encodeURIComponent(shareText + shareUrl)}`;
    window.open(u, "_blank", "noopener,noreferrer");
  };
  const email = () => {
    const subject = encodeURIComponent(title ? `Watch: ${title}` : "Live on Tap IN TV");
    const body = encodeURIComponent(`${shareText}\n${shareUrl}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };
  const linkedin = () => {
    const u = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
    window.open(u, "_blank", "noopener,noreferrer");
  };
  const native = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: title || "Tap IN — LIVE", text: shareText, url: shareUrl }); }
      catch { /* user cancelled */ }
    } else {
      copyLink();
    }
  };

  const btn = "inline-flex items-center gap-1.5 text-white text-xs font-bold px-3 py-1.5 rounded-full border border-white/15 bg-white/5 hover:bg-white/15 transition";

  return (
    <div
      data-testid="go-live-share-bar"
      className="flex flex-wrap items-center gap-2 bg-gradient-to-r from-red-950/70 via-rose-950/60 to-black/60 border border-red-500/30 rounded-xl px-3 py-2"
    >
      {/* Live + viewer count */}
      <div data-testid="go-live-viewer-count" className="inline-flex items-center gap-2 pr-3 mr-1 border-r border-white/15">
        <span className="inline-flex items-center gap-1.5 bg-red-600 text-white text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded-full animate-pulse">
          <span className="w-1.5 h-1.5 rounded-full bg-white" /> LIVE
        </span>
        <Eye className="w-4 h-4 text-white/70" />
        <span className="text-white font-display font-black text-lg leading-none tabular-nums">{count.toLocaleString()}</span>
        {delta > 0 && (
          <span data-testid="go-live-delta" className="text-emerald-300 text-[10px] font-black animate-bounce">+{delta}</span>
        )}
        <span className="text-white/50 text-[10px] uppercase tracking-wider">watching</span>
      </div>

      {/* Share buttons */}
      <button data-testid="share-twitter"  onClick={tweet}    className={btn}>
        <Twitter className="w-3.5 h-3.5" /> X / Twitter
      </button>
      <button data-testid="share-facebook" onClick={fb}       className={btn}>
        <Facebook className="w-3.5 h-3.5" /> Facebook
      </button>
      <button data-testid="share-whatsapp" onClick={whatsapp} className={btn}>
        <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
      </button>
      <button data-testid="share-linkedin" onClick={linkedin} className={btn}>
        <Linkedin className="w-3.5 h-3.5" /> LinkedIn
      </button>
      <button data-testid="share-email"    onClick={email}    className={btn}>
        <Mail className="w-3.5 h-3.5" /> Email
      </button>
      <button data-testid="share-copy"     onClick={copyLink} className={btn}>
        {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
        {copied ? "Copied!" : "Copy link"}
      </button>
      <button data-testid="share-native"   onClick={native}   className={btn + " ml-auto bg-gradient-to-r from-red-500 to-rose-600 border-transparent hover:from-red-400 hover:to-rose-500"}>
        <Share2 className="w-3.5 h-3.5" /> Share
      </button>
    </div>
  );
}
